/**
 * AnalysisToolsContent — extracted body of AccountantAnalysisTools so it can
 * render bare inside the accountant Case Workspace (Defense stage) with no
 * page header / back-nav (showHeader=false), while the standalone
 * /dashboard/accountant/analysis/:reportId route keeps its header.
 *
 * Two on-demand panels over the deterministic engines:
 *   1. Monte Carlo — the confidence interval around the claim.
 *   2. Sensitivity — how the claim moves as one driver varies ±20%.
 *
 * Extract-content pattern (zero net-new dashboards): identical behavior, props,
 * handlers, and data-testids to the original page; the only new seam is
 * { reportId?, showHeader? }.
 */

import React, { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Activity, TrendingUp, Loader2, AlertTriangle, Play, MessageSquare } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';

import { MonteCarloDistribution } from '../../charts/MonteCarloDistribution';
import CaseIntelligenceChat from '../../CaseIntelligenceChat';
import {
  runMonteCarlo,
  runSensitivity,
  type MonteCarloResult,
  type SensitivityResult,
  type SensitivityParameter,
} from '../../../services/analysisApi';
import { formatCurrency } from '../../../utils/formatters';

const whole = (v: number | undefined | null) =>
  formatCurrency(v ?? 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const PARAMETERS: { value: SensitivityParameter; label: string }[] = [
  { value: 'payroll', label: 'Payroll' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'variable_expenses', label: 'Variable expenses' },
];

const ITERATION_OPTIONS = [1000, 2000, 5000];

function errMessage(err: any, fallback: string): string {
  // The causation-failed 400 carries a structured detail; surface its message.
  const detail = err?.detail ?? err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return err?.message || fallback;
}

// A labelled financial figure, right-aligned monospace per the financial-data convention.
function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <dt className="cs-label">{label}</dt>
      <dd
        className="cs-figure mt-1"
        style={{
          fontWeight: 600,
          color: accent ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        }}
      >
        {value}
      </dd>
    </div>
  );
}

export interface AnalysisToolsContentProps {
  reportId?: string;
  showHeader?: boolean;
}

export function AnalysisToolsContent({
  reportId: reportIdProp,
  showHeader = true,
}: AnalysisToolsContentProps) {
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const reportId = reportIdProp ?? reportIdParam;
  const navigate = useNavigate();

  // --- Monte Carlo state ---
  const [iterations, setIterations] = useState(2000);
  const [mc, setMc] = useState<MonteCarloResult | null>(null);
  const [mcLoading, setMcLoading] = useState(false);
  const [mcError, setMcError] = useState<string | null>(null);

  // --- Sensitivity state ---
  const [parameter, setParameter] = useState<SensitivityParameter>('payroll');
  const [sens, setSens] = useState<SensitivityResult | null>(null);
  const [sensLoading, setSensLoading] = useState(false);
  const [sensError, setSensError] = useState<string | null>(null);

  // --- Forensic AI assistant (CPA-scoped) ---
  // Demoted from the always-on orange FAB to a hueless inline launcher (K3 pass):
  // the accountant↔AI chat is scoped to calculation/forensic tools (no attorney
  // work-product). This stays the CPA's only in-workspace access to it.
  const [showChat, setShowChat] = useState(false);

  const onRunMonteCarlo = useCallback(async () => {
    if (!reportId) return;
    setMcLoading(true);
    setMcError(null);
    try {
      setMc(await runMonteCarlo(reportId, { iterations }));
    } catch (err: any) {
      setMcError(errMessage(err, 'Failed to run Monte Carlo simulation'));
    } finally {
      setMcLoading(false);
    }
  }, [reportId, iterations]);

  const onRunSensitivity = useCallback(async () => {
    if (!reportId) return;
    setSensLoading(true);
    setSensError(null);
    try {
      setSens(await runSensitivity(reportId, { parameter }));
    } catch (err: any) {
      setSensError(errMessage(err, 'Failed to run sensitivity analysis'));
    } finally {
      setSensLoading(false);
    }
  }, [reportId, parameter]);

  return (
    <div
      className={showHeader ? 'min-h-screen' : ''}
      style={{ background: 'var(--color-bg-base)' }}
      data-testid="analysis-tools"
    >
      {showHeader && (
        <div className="px-6 py-4" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
          <div className="mx-auto max-w-5xl">
            <button
              onClick={() => navigate(`/dashboard/accountant/review/${reportId}`)}
              className="cs-meta mb-3 flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Engine Review
            </button>
            <h1 className="cs-h2">Analysis Tools</h1>
            <p className="cs-meta mt-1">
              Stress-test the supportable number: a confidence interval around the claim and the
              driver sensitivities behind it — the evidence that defends the figure under scrutiny.
            </p>
            <Link
              to={`/dashboard/accountant/methodology/${reportId}`}
              data-testid="open-methodology-view"
              className="cs-chip mt-3 inline-flex items-center gap-1.5"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              View the methodology comparison →
            </Link>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-5xl space-y-8 px-6 py-6">
        {/* ---------------------------------------------------------------- */}
        {/* Monte Carlo */}
        {/* ---------------------------------------------------------------- */}
        <section
          className="rounded-[2px] p-5"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          data-testid="mc-panel"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 rounded-[2px] p-2" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>
                <Activity className="h-5 w-5" />
              </span>
              <div>
                <h2 className="cs-h3">
                  Monte Carlo confidence interval
                </h2>
                <p className="cs-meta mt-1 max-w-xl">
                  Re-runs the loss engine across many simulations with ±15% Gaussian variation in
                  revenue to show the 95% confidence band around the claim.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="mc-iterations">
                Iterations
              </label>
              <select
                id="mc-iterations"
                data-testid="mc-iterations"
                value={iterations}
                onChange={(e) => setIterations(Number(e.target.value))}
                disabled={mcLoading}
                className="cs-body rounded-[2px] px-2.5 py-1.5 disabled:opacity-50"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
              >
                {ITERATION_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n.toLocaleString()} runs
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={onRunMonteCarlo}
                disabled={mcLoading}
                data-testid="mc-run"
                className="cs-body inline-flex items-center gap-1.5 rounded-[2px] px-3 py-1.5 disabled:opacity-50"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}
              >
                {mcLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                {mc ? 'Re-run' : 'Run simulation'}
              </button>
            </div>
          </div>

          <div className="mt-5">
            {mcLoading && (
              <div
                className="cs-meta flex items-center gap-2"
                data-testid="mc-loading"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                Running {iterations.toLocaleString()} simulations…
              </div>
            )}

            {!mcLoading && mcError && (
              <div
                className="cs-body flex items-start gap-2 rounded-[2px] px-4 py-3"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-blocker)' }}
                data-testid="mc-error"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{mcError}</span>
              </div>
            )}

            {!mcLoading && !mcError && !mc && (
              <p className="cs-meta" style={{ color: 'var(--color-text-dim)' }} data-testid="mc-empty">
                Run a simulation to see the confidence interval around the claim.
              </p>
            )}

            {!mcLoading && !mcError && mc && (
              <div data-testid="mc-result">
                <dl
                  className="grid grid-cols-2 gap-4 sm:grid-cols-4"
                  data-testid="mc-stats"
                >
                  <Stat label="Base claim" value={whole(mc.base_claim)} accent />
                  <Stat label="Mean" value={whole(mc.results.mean)} />
                  <Stat label="Median" value={whole(mc.results.median)} />
                  <Stat label="Std dev" value={whole(mc.results.std_dev)} />
                  <div className="col-span-2 sm:col-span-4">
                    <dt className="cs-label">
                      95% confidence interval
                    </dt>
                    <dd className="cs-figure mt-1" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                      {whole(mc.results.ci_95_low)} – {whole(mc.results.ci_95_high)}
                    </dd>
                  </div>
                </dl>
                <div className="mt-5 overflow-x-auto">
                  <MonteCarloDistribution
                    data={mc.distribution_sample}
                    mean={mc.results.mean}
                    median={mc.results.median}
                    p5={mc.results.ci_95_low}
                    p95={mc.results.ci_95_high}
                    baseDemand={mc.base_claim}
                    title="Simulated claim-value distribution"
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Sensitivity */}
        {/* ---------------------------------------------------------------- */}
        <section
          className="rounded-[2px] p-5"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
          data-testid="sens-panel"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 rounded-[2px] p-2" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>
                <TrendingUp className="h-5 w-5" />
              </span>
              <div>
                <h2 className="cs-h3">
                  Driver sensitivity
                </h2>
                <p className="cs-meta mt-1 max-w-xl">
                  Holds everything else fixed and varies one driver from 80% to 120% of its value,
                  showing how far the claim swings — i.e. where the number is most exposed.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="sens-parameter">
                Driver
              </label>
              <select
                id="sens-parameter"
                data-testid="sens-param"
                value={parameter}
                onChange={(e) => setParameter(e.target.value as SensitivityParameter)}
                disabled={sensLoading}
                className="cs-body rounded-[2px] px-2.5 py-1.5 disabled:opacity-50"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
              >
                {PARAMETERS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={onRunSensitivity}
                disabled={sensLoading}
                data-testid="sens-run"
                className="cs-body inline-flex items-center gap-1.5 rounded-[2px] px-3 py-1.5 disabled:opacity-50"
                style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}
              >
                {sensLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                {sens ? 'Re-run' : 'Run sensitivity'}
              </button>
            </div>
          </div>

          <div className="mt-5">
            {sensLoading && (
              <div
                className="cs-meta flex items-center gap-2"
                data-testid="sens-loading"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                Sweeping the claim across the driver range…
              </div>
            )}

            {!sensLoading && sensError && (
              <div
                className="cs-body flex items-start gap-2 rounded-[2px] px-4 py-3"
                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-blocker)' }}
                data-testid="sens-error"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{sensError}</span>
              </div>
            )}

            {!sensLoading && !sensError && !sens && (
              <p className="cs-meta" style={{ color: 'var(--color-text-dim)' }} data-testid="sens-empty">
                Pick a driver and run the sweep to see how the claim responds.
              </p>
            )}

            {!sensLoading && !sensError && sens && (
              <div data-testid="sens-result">
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="sens-stats">
                  <Stat label="Base claim" value={whole(sens.base_claim)} accent />
                  <Stat label="Low" value={whole(sens.range.min)} />
                  <Stat label="High" value={whole(sens.range.max)} />
                  <Stat label="Swing" value={whole(sens.range.spread)} />
                </dl>
                <div className="mt-5" data-testid="sens-chart">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={sens.results}
                      margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis
                        dataKey="pct"
                        tickFormatter={(v) => `${v}%`}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}k`}
                        tick={{ fontSize: 12 }}
                        width={64}
                      />
                      <Tooltip
                        formatter={(v: any) => [whole(Number(v)), 'Claim']}
                        labelFormatter={(l) => `${l}% of ${parameter.replace(/_/g, ' ')}`}
                      />
                      <ReferenceLine y={sens.base_claim} stroke="var(--color-text-dim)" strokeDasharray="4 4" />
                      <Line
                        type="monotone"
                        dataKey="claim"
                        stroke="var(--color-brand)"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Forensic AI assistant — CPA-scoped (calculation/forensic tools, no
            attorney work-product). Demoted from the always-on orange FAB to a
            hueless inline launcher (K3 pass); the chat panel opens on click. */}
        {reportId && (
          <div>
            {!showChat && (
              <button
                type="button"
                onClick={() => setShowChat(true)}
                data-testid="open-intelligence-chat"
                title="Forensic assistant — calculation & forensic scope"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:opacity-80"
                style={{
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 2,
                  backgroundColor: 'transparent',
                }}
              >
                <MessageSquare className="w-4 h-4" aria-hidden="true" />
                Forensic assistant
              </button>
            )}
            <CaseIntelligenceChat
              reportId={reportId}
              isOpen={showChat}
              onClose={() => setShowChat(false)}
              toolScope="accountant"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default AnalysisToolsContent;
